import { useAuth } from '@clerk/clerk-react';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiLink } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import { formatMoney } from '@/lib/utils';
import type { Payment, PaymentListResponse, PaymentMethodListResponse } from '@/lib/types';

export type UnlinkedPaymentOption = Pick<
  Payment,
  'id' | 'amount_cents' | 'payment_date' | 'payment_method' | 'note' | 'expense_id' | 'disputes'
>;

type UnlinkedPaymentPickerProps = {
  onSelect: (payment: UnlinkedPaymentOption) => void;
  maxItems?: number;
  disabled?: boolean;
};

export function UnlinkedPaymentPicker({
  onSelect,
  maxItems = 8,
  disabled = false,
}: UnlinkedPaymentPickerProps) {
  const { getToken } = useAuth();

  const paymentsQuery = useQuery({
    queryKey: ['payments', 'unlinked'],
    queryFn: () => apiJson<PaymentListResponse>(getToken, '/v1/payments'),
  });

  const paymentMethodsQuery = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () =>
      apiJson<PaymentMethodListResponse>(getToken, '/v1/payment-methods'),
  });

  const paymentMethodNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const method of paymentMethodsQuery.data?.payment_methods ?? []) {
      map.set(method.payment_method_id, method.name);
    }
    return map;
  }, [paymentMethodsQuery.data?.payment_methods]);

  const unlinkedPayments = useMemo(() => {
    return (paymentsQuery.data?.payments ?? [])
      .filter(
        (payment) =>
          payment.expense_id == null && (payment.disputes ?? []).length === 0
      )
      .sort(
        (a, b) =>
          new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
      )
      .slice(0, maxItems);
  }, [maxItems, paymentsQuery.data?.payments]);

  if (paymentsQuery.isLoading) {
    return (
      <div className='py-4 flex justify-center'>
        <div className='animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (paymentsQuery.isError) {
    return (
      <p className='text-sm text-gray-500'>Couldn&apos;t load recent payments.</p>
    );
  }

  if (unlinkedPayments.length === 0) {
    return (
      <p className='text-sm text-gray-500'>
        No unlinked payments yet. Record a new one below.
      </p>
    );
  }

  return (
    <div className='space-y-2'>
      {unlinkedPayments.map((payment) => {
        const methodName =
          paymentMethodNames.get(payment.payment_method) ??
          payment.payment_method;

        return (
          <button
            key={payment.id}
            type='button'
            disabled={disabled}
            onClick={() => onSelect(payment)}
            className='w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-white hover:border-gray-300 text-left transition-colors disabled:opacity-60'
          >
            <div className='flex-1 min-w-0'>
              <p className='font-bold text-emerald-700'>
                {formatMoney(payment.amount_cents)}
              </p>
              <p className='text-sm text-gray-500 truncate'>
                {new Date(payment.payment_date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
                {' · '}
                {methodName}
                {payment.note ? ` · ${payment.note}` : ''}
              </p>
            </div>
            <FiLink className='w-5 h-5 text-primary shrink-0' />
          </button>
        );
      })}
    </div>
  );
}
